import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import { attendeeEventsApi } from '../events/attendee-events.api';
import {
  GetAttendeeFavoritesResponse,
  AttendeeFavoritesFilter,
  AttendeeFavoriteItem,
  AddFavoriteRequest,
  ChangeFavoriteStatusRequest,
} from './models';

const attendee_favorites = 'attendee_favorites';

export const attendeeFavoritesApi = createApi({
  reducerPath: attendee_favorites,
  baseQuery: baseQueryWithReauth,
  tagTypes: [attendee_favorites],
  endpoints: (build) => ({
    getAttendeeFavorites: build.query<GetAttendeeFavoritesResponse, AttendeeFavoritesFilter>({
      query: (filters) => {
        const params = new URLSearchParams();

        if (filters?.search && filters.search.length > 2) {
          params.append('filters[search]', filters.search);
        }

        if (Array.isArray(filters?.event_ids)) {
          filters.event_ids.forEach((id) => {
            params.append('filters[event_ids][]', id);
          });
        }

        if (Array.isArray(filters?.statuses)) {
          filters.statuses.forEach((status) => {
            params.append('filters[statuses][]', status.toString());
          });
        }

        if (typeof filters?.is_matched === 'boolean') {
          params.append('filters[is_matched]', filters.is_matched ? '1' : '0');
        }

        if (filters?.page) {
          params.append('page', filters.page.toString());
        }

        const queryString = params.toString();

        return {
          url: `/attendee/favorites${queryString ? '?' + queryString : ''}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: attendee_favorites, id: 'LIST' }],
    }),

    addFavorite: build.mutation<{ data: AttendeeFavoriteItem }, AddFavoriteRequest>({
      query: (body) => ({
        url: '/attendee/favorites',
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: attendee_favorites, id: 'LIST' }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            attendeeEventsApi.util.invalidateTags([{ type: 'attendee_events', id: 'LIST' }]),
          );
        } catch {
          /* mutation failed */
        }
      },
    }),

    changeFavoriteStatus: build.mutation<
      { data: AttendeeFavoriteItem },
      ChangeFavoriteStatusRequest
    >({
      query: ({ id, status, transition }) => ({
        url: `/attendee/favorites/${id}/status/${status}`,
        method: 'PATCH',
        body: { transition },
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: attendee_favorites, id: 'LIST' }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            attendeeEventsApi.util.invalidateTags([{ type: 'attendee_events', id: 'LIST' }]),
          );
        } catch {
          /* mutation failed */
        }
      },
    }),
  }),
});

export const {
  useGetAttendeeFavoritesQuery,
  useAddFavoriteMutation,
  useChangeFavoriteStatusMutation,
} = attendeeFavoritesApi;
